import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import { Select, Autocomplete, InputText, IconChangeLoClock } from '@ui/src';
import { useAppSelector } from '@store';
import {
  FeeTypeEnum,
  FeeSubTypeEnum,
  FeeTransactionTypeEnum,
} from '../../hoc/fees/types';

import cn from './fees.module.css';
import { FeeFormTypes } from './types';

const FeeForm = ({
  control,
  workMethod,
  condition,
  errors,
  feeId,
  isEditable,
  feeTransactionType,
  accounts,
  onSearch,
  isLoadingSearch,
  paymentTypes,
  auditLogAvailable,
}: FeeFormTypes) => {
  const { baseCurrency } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  return (
    <div className={cn.feeForm}>
      <div className={cn.feeFormGroup}>
        <Controller
          control={control}
          name="name"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder="Enter fee type name"
              onChange={onChange}
              value={value}
              title="Fee type name"
              error={errors?.name?.message}
              tabIndex={feeId && !isEditable ? -1 : 0}
              disabled={Boolean(feeId && !isEditable)}
            />
          )}
        />
      </div>
      <div className={cn.feeFormGroup}>
        <Controller
          control={control}
          name="feeType"
          render={({ field: { onChange, value, name } }) => (
            <Select
              list={[
                {
                  name: 'Transaction',
                  value: FeeTypeEnum.TRANSACTION,
                },
                {
                  name: 'Regular',
                  value: FeeTypeEnum.REGULAR,
                },
                {
                  name: 'Manual',
                  value: FeeTypeEnum.MANUAL,
                },
              ]}
              placeholder="Select method"
              name={name}
              value={value}
              onChange={onChange}
              title="Work method"
              selectWrapperClassName={`${cn.feeFormSelect} ${
                errors?.feeType?.message ? cn.feeFormSelectError : ''
              }`}
              disabled={Boolean(feeId)}
              tabIndex={feeId ? -1 : 0}
              fullWidth
            />
          )}
        />
        {errors?.feeType?.message && (
          <div className={cn.error}>{errors?.feeType?.message}</div>
        )}
      </div>
      <div className={cn.feeFormGroup}>
        <Controller
          control={control}
          name="feeTargetAccountNumber"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              name={name}
              title="Target account"
              placeholder="Select account..."
              value={value}
              onChange={onChange}
              list={accounts}
              onSearch={onSearch}
              disabled={Boolean(feeId && !isEditable)}
              tabIndex={feeId && !isEditable ? -1 : 0}
              isLoading={isLoadingSearch}
              error={errors?.feeTargetAccountNumber?.message}
              fullWidth
              withSuffix
            />
          )}
        />
      </div>
      {workMethod === FeeTypeEnum.TRANSACTION && (
        <>
          <div className={cn.feeFormGroup}>
            <Controller
              control={control}
              defaultValue=""
              name="feeTransactionType"
              render={({ field: { onChange, value, name } }) => (
                <Select
                  list={[
                    {
                      name: 'Outgoing',
                      value: FeeTransactionTypeEnum.OUTGOING,
                    },
                    {
                      name: 'Incoming',
                      value: FeeTransactionTypeEnum.INCOMING,
                    },
                    {
                      name: 'Internal',
                      value: FeeTransactionTypeEnum.INTERNAL,
                    },
                  ]}
                  placeholder="Select transaction type"
                  name={name}
                  value={value}
                  onChange={onChange}
                  title="Transaction type"
                  error={errors?.feeTransactionType?.message}
                  disabled={Boolean(feeId)}
                  tabIndex={feeId ? -1 : 0}
                  fullWidth
                />
              )}
            />
          </div>
          {(feeTransactionType === FeeTransactionTypeEnum.INCOMING ||
            feeTransactionType === FeeTransactionTypeEnum.OUTGOING) && (
            <div className={cn.feeFormGroup}>
              <Controller
                control={control}
                name="paymentType"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    list={paymentTypes || []}
                    placeholder="Select payment type"
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Payment type"
                    selectWrapperClassName={`${cn.feeFormSelect} ${
                      errors?.paymentType?.message ? cn.feeFormSelectError : ''
                    }`}
                    disabled={Boolean(feeId)}
                    tabIndex={feeId ? -1 : 0}
                    fullWidth
                  />
                )}
              />
              {errors?.paymentType?.message && (
                <div className={cn.error}>{errors?.paymentType?.message}</div>
              )}
            </div>
          )}
        </>
      )}
      {workMethod === FeeTypeEnum.REGULAR && (
        <>
          <div className={cn.feeFormGroup}>
            <Controller
              control={control}
              name="regularFeeSubType"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <Select
                  list={[
                    {
                      name: 'High-Balance fee',
                      value: FeeSubTypeEnum.HIGH_BALANCE,
                    },
                    {
                      name: 'Account inactivity fee',
                      value: FeeSubTypeEnum.INACTIVITY,
                    },
                    {
                      name: 'Regular package fee',
                      value: FeeSubTypeEnum.REGULAR,
                    },
                  ]}
                  placeholder="Select sub type"
                  name={name}
                  value={value}
                  onChange={onChange}
                  title="Fee Sub type"
                  selectWrapperClassName={`${cn.feeFormSelect} ${
                    errors?.regularFeeSubType?.message
                      ? cn.feeFormSelectError
                      : ''
                  }`}
                  disabled={Boolean(feeId)}
                  tabIndex={feeId ? -1 : 0}
                  fullWidth
                />
              )}
            />
            {errors?.regularFeeSubType?.message && (
              <div className={cn.error}>
                {errors?.regularFeeSubType?.message}
              </div>
            )}
          </div>
          {condition === FeeSubTypeEnum.INACTIVITY && (
            <div className={cn.feeFormGroup}>
              <Controller
                control={control}
                name="regularInactivityMonthsAmount"
                defaultValue="1"
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    name={name}
                    placeholder="Enter inactivity months"
                    onChange={(e) => {
                      onChange(
                        e.target.value
                          .replace(/[^0-9-]/g, '')
                          .replace(/(?!^)-/g, ''),
                      );

                      if (Number(e.target.value) < 0) {
                        onChange(0);
                      }
                    }}
                    value={value}
                    title="Inactivity months"
                    error={errors?.regularInactivityMonthsAmount?.message}
                    tabIndex={feeId && !isEditable ? -1 : 0}
                    disabled={Boolean(feeId && !isEditable)}
                  />
                )}
              />
            </div>
          )}
          {condition === FeeSubTypeEnum.HIGH_BALANCE && (
            <div className={cn.feeFormGroup}>
              <Controller
                control={control}
                name="regularHighBalanceAmount"
                defaultValue=""
                render={({ field: { onChange, value, name } }) => (
                  <InputText
                    name={name}
                    placeholder="Enter high balance amount"
                    onChange={onChange}
                    value={value}
                    suffix={baseCurrency?.name}
                    className={cn.writeOffInput}
                    title="High balance amount"
                    error={errors?.regularHighBalanceAmount?.message}
                    disabled={Boolean(feeId)}
                    tabIndex={feeId ? -1 : 0}
                    currency={baseCurrency}
                    amountInput
                  />
                )}
              />
            </div>
          )}
        </>
      )}
      {auditLogAvailable && feeId && (
        <NavLink
          to={`/administration/change-logs?entityType=FEE&entityId=${feeId}`}
          target="_blank"
          className={cn.changeLogPanelLink}
        >
          <IconChangeLoClock />
          Change Log
        </NavLink>
      )}
    </div>
  );
};

export default FeeForm;
